import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { userManager } from '../oidcAuth';

export class ApiClient {
  private axiosInstance: AxiosInstance;
  private tokenType: 'access_token' | 'id_token';

  constructor(baseUrl: string, tokenType: 'access_token' | 'id_token' = 'access_token') {
    this.tokenType = tokenType;
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      withCredentials: true, /* Equivalente ao { withCredentials: true } da LEX */
      headers: { 'Content-Type': 'application/json' },
    });

    this.axiosInstance.interceptors.request.use(async (config) => {
      const user = await userManager.getUser();
      const token = user?.[this.tokenType];
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.error("API returned 401. Dispatching sessionExpired event and swallowing error.");
          window.dispatchEvent(new CustomEvent('sessionExpired'));
          // By returning a non-resolving promise, we prevent the original caller 
          // from seeing a thrown error, which avoids an unhandled promise rejection.
          return new Promise(() => { });
        }
        // For all other errors, we still reject.
        return Promise.reject(error);
      }
    );
  }

  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(endpoint, config);
    if (response.status === 204) return { data: [] } as T;
    return response.data;
  }

  async post<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(endpoint, data, config);
    return response.data;
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    const response = await this.axiosInstance.put<T>(endpoint, data);
    return response.data;
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await this.axiosInstance.patch<T>(endpoint, data);
    return response.data;
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(endpoint, config);
    return response.data;
  }
}
